import React from 'react';
import { Box, Heading } from '@wix/design-system';
import { RecommendationLabel } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { parseDomain } from '@wix/domains-serverless-interface';
import { ComBrRestriction, DomainWithEllipsis, PricingSection, SearchRecommendationLayout } from '@wix/domains-search-suite';
import { useIsMobile } from '../../hooks/useIsMobile';
import { AvailableNowBadge } from './AvailableNowBadge';
import { MatchLabelBadge } from './MatchLabelBadge';
import { HeroCtaButton } from './HeroCtaButton';
import { HeroBusinessEmailExample } from './HeroBusinessEmailExample';
import s from './HeroCard.css';
export const CompactHeroCard = _ref => {
  let {
    recommendation,
    onClickGetIt,
    disabled = false,
    dataHook = 'hero-card',
    isResultsPageOptimizations = false,
    Label,
    badgeLabel,
    ctaPriority = 'primary',
    businessEmailExample = false,
    isPricingLoading = false,
    PriceNote
  } = _ref;
  const isMobile = useIsMobile();
  const {
    domain = '',
    label,
    price
  } = recommendation;
  const {
    tld
  } = parseDomain(domain);
  const isExactMatch = label === RecommendationLabel.EXACT_MATCH;
  const matchBadgeLabel = isExactMatch ? label : badgeLabel;
  const showMatchBadge = isExactMatch || Boolean(badgeLabel);
  const ctaButton = /*#__PURE__*/React.createElement(HeroCtaButton, {
    size: "medium",
    priority: ctaPriority,
    disabled: disabled,
    onClick: () => onClickGetIt == null ? void 0 : onClickGetIt(recommendation),
    dataHook: `${dataHook}-cta`
  });
  const badgesRow = /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP2",
    verticalAlign: "middle",
    flexWrap: "wrap"
  }, showMatchBadge && matchBadgeLabel && (Label && matchBadgeLabel !== 'RECOMMENDED' ? /*#__PURE__*/React.createElement(Label, {
    primary: true,
    label: matchBadgeLabel,
    domain: domain,
    size: isMobile ? 'tiny' : 'small'
  }) : /*#__PURE__*/React.createElement(MatchLabelBadge, {
    label: matchBadgeLabel,
    optimized: !isExactMatch,
    primary: ctaPriority === 'primary'
  })), isExactMatch && /*#__PURE__*/React.createElement(AvailableNowBadge, null));
  const titleHeading = isExactMatch ? /*#__PURE__*/React.createElement(SearchRecommendationLayout.TitleDisplay, {
    title: domain,
    exactMatch: true,
    isResultsPageOptimizations: isResultsPageOptimizations
  }) : /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px"
  }, /*#__PURE__*/React.createElement(DomainWithEllipsis, {
    maxWidth: isMobile ? '310px' : '440px',
    textComponent: Heading,
    textComponentProps: {
      size: isMobile ? 'large' : 'extraLarge',
      as: 'h1'
    },
    className: isMobile ? s.compactDomainMobile : s.compactDomain,
    dataHook: `${dataHook}-title`
  }, domain), /*#__PURE__*/React.createElement(ComBrRestriction, {
    domainName: domain
  }));
  const compactSection = /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    padding: "SP6",
    gap: isMobile && isResultsPageOptimizations ? '16px' : 'SP6',
    flex: 1
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: isMobile && isResultsPageOptimizations ? '16px' : '8px',
    minWidth: 0
  }, badgesRow, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px",
    minWidth: 0
  }, titleHeading, businessEmailExample && /*#__PURE__*/React.createElement(HeroBusinessEmailExample, {
    domain: domain,
    size: "small"
  }), PriceNote && /*#__PURE__*/React.createElement(PriceNote, {
    price: price,
    domain: domain
  }))), /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    align: "space-between",
    verticalAlign: "bottom",
    gap: "SP4",
    flexWrap: "wrap",
    marginTop: "auto"
  }, /*#__PURE__*/React.createElement(PricingSection, {
    pricing: price,
    tld: tld,
    domainName: domain,
    useMobileLayout: true,
    isResultsPageOptimizations: isResultsPageOptimizations,
    matchStrikethroughSize: isMobile && isResultsPageOptimizations,
    isPricingLoading: isPricingLoading
  }), /*#__PURE__*/React.createElement(Box, {
    flexShrink: 0,
    marginLeft: "auto"
  }, ctaButton)));
  const compactExactSection = /*#__PURE__*/React.createElement(Box, {
    direction: isMobile ? 'vertical' : 'horizontal',
    align: isMobile ? undefined : 'space-between',
    verticalAlign: isMobile ? undefined : 'middle',
    padding: isMobile ? '24px' : '36px 24px',
    gap: isMobile && isResultsPageOptimizations ? '16px' : 'SP4'
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: isMobile && isResultsPageOptimizations ? '16px' : isResultsPageOptimizations ? '12px' : '8px',
    align: "left",
    minWidth: 0
  }, badgesRow, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px",
    minWidth: 0
  }, titleHeading, businessEmailExample && /*#__PURE__*/React.createElement(HeroBusinessEmailExample, {
    domain: domain,
    size: "small"
  }), PriceNote && /*#__PURE__*/React.createElement(PriceNote, {
    price: price,
    domain: domain
  }))), /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    verticalAlign: "middle",
    gap: "SP4",
    flexShrink: 0,
    width: isMobile ? '100%' : undefined,
    align: isMobile ? 'space-between' : undefined
  }, /*#__PURE__*/React.createElement(PricingSection, {
    pricing: price,
    tld: tld,
    domainName: domain,
    isResultsPageOptimizations: false,
    isPricingLoading: isPricingLoading
  }), ctaButton));
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: !isExactMatch && !isMobile ? undefined : '100%',
    flex: !isExactMatch && !isMobile ? 1 : undefined,
    minWidth: !isExactMatch && !isMobile ? 0 : undefined,
    borderRadius: "12px",
    overflow: "hidden",
    className: isResultsPageOptimizations ? s.card : s.cardShadow,
    dataHook: dataHook
  }, isExactMatch ? compactExactSection : compactSection);
};
//# sourceMappingURL=CompactHeroCard.js.map